/**
 * Sign-in requests hold the refresh lock (D1042).
 *
 * POST /v1/auth/login and /v1/auth/mfa/verify carry this browser's refresh
 * cookie (its Path is /v1/auth). When one opens a session, the server ends
 * the session that cookie names and answers a new cookie over it. That
 * spends the cookie as a refresh does, so a sign-in takes the same Web Lock
 * every tab's refresh takes (`REFRESH_LOCK`, lib/auth/refresh.ts):
 *
 * - a refresh another tab already has in flight finishes first, and the
 *   sign-in then sends the cookie that refresh was given;
 * - a refresh asked for while the sign-in runs waits, and then sends the
 *   cookie the sign-in set, which names the new session that tab follows.
 *
 * Without the lock the two answers could land in either order, and the jar
 * could keep the rotated cookie of the session the sign-in had just ended:
 * every tab would then be signed out at its next refresh. Where Web Locks are
 * missing, the request is sent as it is (as refresh.ts does).
 *
 * The caller keeps its own post call, path and all, inside `send`, so the
 * contract audit still reads each sign-in path where it is called.
 */
import { REFRESH_LOCK } from "@/lib/auth/refresh";

/** Send a sign-in step while holding the refresh lock. */
export async function whileSigningIn<T>(send: () => Promise<T>): Promise<T> {
  const locks = lockManager();
  if (!locks) return send();
  return locks.request(REFRESH_LOCK, { mode: "exclusive" }, send);
}

function lockManager(): LockManager | undefined {
  try {
    const locks = globalThis.navigator?.locks;
    return typeof locks?.request === "function" ? locks : undefined;
  } catch {
    return undefined;
  }
}
